import { FC, useEffect, useRef, useState } from 'react';

import { Box, Button, HStack, IconButton, Input, VisuallyHidden } from '@chakra-ui/react';
import { LuMinus, LuPlus, LuRedo2, LuUndo2 } from 'react-icons/lu';

import { parseZoomPercentInput, zoomToPercent } from '../../canvas/viewport';
import type { EditorCanvasSession } from './useEditorCanvasSession';

type Props = {
  session: EditorCanvasSession;
};

const chipProps = {
  bg: 'white',
  borderRadius: 'md',
  boxShadow: 'sm',
  border: '1px solid',
  borderColor: 'blackAlpha.100',
  spacing: 0,
  pointerEvents: 'auto' as const,
};

export const EditorCanvasControls: FC<Props> = ({ session }) => {
  const [draftPercent, setDraftPercent] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const draftPercentRef = useRef<string | null>(null);
  const displayPercent = zoomToPercent(session.viewport.zoom);
  const isEditingZoom = draftPercent !== null;

  useEffect(() => {
    if (!isEditingZoom) {
      return;
    }
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditingZoom]);

  const startEditing = () => {
    const next = String(displayPercent);
    draftPercentRef.current = next;
    setDraftPercent(next);
  };

  const commitPercent = () => {
    const value = draftPercentRef.current;
    draftPercentRef.current = null;
    setDraftPercent(null);
    if (value === null) {
      return;
    }
    const parsed = parseZoomPercentInput(value);
    if (parsed != null) {
      session.setZoom(parsed);
    }
  };

  const cancelPercent = () => {
    draftPercentRef.current = null;
    setDraftPercent(null);
  };

  return (
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      zIndex={1}
      data-test-id="editor-canvas-controls"
    >
      <HStack
        {...chipProps}
        position="absolute"
        left="50%"
        bottom="12px"
        transform="translateX(-50%)"
        data-test-id="editor-canvas-history-controls"
      >
        <IconButton
          aria-label="Undo"
          icon={<LuUndo2 />}
          size="sm"
          variant="ghost"
          onClick={session.undo}
          isDisabled={session.undoDepth === 0}
          data-test-id="editor-canvas-undo"
        />
        <IconButton
          aria-label="Redo"
          icon={<LuRedo2 />}
          size="sm"
          variant="ghost"
          onClick={session.redo}
          isDisabled={session.redoDepth === 0}
          data-test-id="editor-canvas-redo"
        />
        <VisuallyHidden data-test-id="editor-canvas-undo-depth">
          Undo {session.undoDepth} / Redo {session.redoDepth}
        </VisuallyHidden>
      </HStack>
      <HStack
        {...chipProps}
        position="absolute"
        right="12px"
        bottom="12px"
        data-test-id="editor-canvas-zoom-controls"
      >
        <IconButton
          aria-label="Zoom out"
          icon={<LuMinus />}
          size="sm"
          variant="ghost"
          onClick={session.zoomOut}
          isDisabled={!session.canZoomOut}
          data-test-id="editor-canvas-zoom-out"
        />
        {draftPercent !== null ? (
          <Input
            ref={inputRef}
            size="sm"
            width="56px"
            px={1}
            textAlign="center"
            value={draftPercent}
            aria-label="Zoom percent"
            data-test-id="editor-canvas-zoom-input"
            onChange={(event) => {
              draftPercentRef.current = event.target.value;
              setDraftPercent(event.target.value);
            }}
            onBlur={commitPercent}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitPercent();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                cancelPercent();
              }
            }}
          />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            minW="48px"
            px={1}
            fontWeight="medium"
            aria-label="Zoom percent"
            data-test-id="editor-canvas-zoom-percent"
            onClick={startEditing}
          >
            {displayPercent}%
          </Button>
        )}
        <IconButton
          aria-label="Zoom in"
          icon={<LuPlus />}
          size="sm"
          variant="ghost"
          onClick={session.zoomIn}
          isDisabled={!session.canZoomIn}
          data-test-id="editor-canvas-zoom-in"
        />
      </HStack>
    </Box>
  );
};

EditorCanvasControls.displayName = 'EditorCanvasControls';
